import {
    skinVars,
    Text6,
    Text3,
    BoxedRow,
    IconShieldCrossRegular,
    Tag,
    ButtonPrimary,
    ButtonLink,
    RowList,
    Row,
    useWindowSize,
    IconInvoicePlanFileRegular,
    IconPinRegular,
    Circle,
} from '@telefonica/mistica';
import { t } from 'i18next';
import { showSnackbar } from '../../utils/store/features/user';
import { ETypeUser } from '../../utils/store/model/store.model';
import './index.scss';
import { getCustomerData } from '../../services/CustomerService';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useUserlight from '../../utils/hooks/useUserLight';
import { managementEnvironment } from '../../utils/dataTransformation/environments';
import { useQuery } from '@tanstack/react-query';
import useGlobalEscalation from '../../utils/globals/GlobalEscalation';
import { isIOS } from 'react-device-detect';

interface IActionProfile {
    icon: JSX.Element;
    title: string;
    description?: string;
    idList?: string;
    enabled: boolean;
    badge?: boolean;
    redirection: {
        url: string;
    };
}

const Profile: React.FC = () => {
    const size = useWindowSize();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const typeUser = new URLSearchParams(window.location.search).get('clientType');
    const isLight = useUserlight();
    const { checkEscalation } = useGlobalEscalation();
    const { data: customerData } = useQuery(['customer-data'], getCustomerData);

    //Enviroment variables stoppers and ObD
    const personalDataDisabled = managementEnvironment().REACT_APP_STOPPER_PROFILE_CUSTOMER_DATA_DISABLED;
    const obdEnabled = managementEnvironment().REACT_APP_WEBVIEW_PROFILE_OBD_ENABLED;

    const actions: IActionProfile[] = [
        {
            icon: <IconInvoicePlanFileRegular size={24} color={skinVars.colors.brand} />,
            title: t('profilePersonalInformation'),
            description: t('profilePersonalDataDescription'),
            badge:
                customerData?.data.contactNumber?.verified || customerData?.data.email?.verified
                    ? false
                    : typeUser === ETypeUser.PRECLIENT
                    ? false
                    : true,

            idList: 'personal-data',
            enabled: !personalDataDisabled,
            redirection: {
                url: '/profile/personal-data',
            },
        },

        {
            icon: <IconPinRegular size={24} color={skinVars.colors.brand} />,
            title: t('profileChangePassword'),
            idList: 'change-password',
            enabled: true,
            redirection: {
                url: '/profile/update-password?flow=changePassword',
            },
        },
    ];

    const handleNavigation = (url: string) => {
        if (url === '/profile/update-password?flow=changePassword') {
            if (isLight) {
                dispatch(showSnackbar());
            } else {
                if (isIOS) {
                    const newWindow = window.open('', 'newWindow');
                    sessionStorage.setItem('newWindow', newWindow?.name || '');
                }
                checkEscalation('/profile/update-password', 'changePassword', {
                    mobileVerified: customerData?.data.contactNumber?.verified,
                    emailVerified: customerData?.data.email?.verified,
                });
            }
        } else {
            navigate(`${url}${typeUser ? `?clientType=${typeUser}` : ''}`);
        }
    };

    const onboardingDaon = () => {
        window.open(`${window.location.origin}/obd/daon?functionality=verification`, '_blank');
        window.addEventListener('message', (event) => {
            if (event.data === 'escalated') {
                window.location.reload();
            }
        });
    };

    return (
        <div className='profile-page'>
            <div className='profile-page__headerBrowser' style={{ background: skinVars.colors.backgroundBrand }}>
                <div
                    className={
                        customerData?.data.name ? 'profile-page__header-section' : 'profile-page__header-section-cif'
                    }
                >
                    <Text6 color={skinVars.colors.textPrimaryInverse}>{t('profileTitle')}</Text6>
                </div>

                {customerData?.data.name && (
                    <div className='profile-page__header-name'>
                        <Text3 regular color={skinVars.colors.textPrimaryInverse}>
                            {t('profileHeadline')}
                        </Text3>
                        <Text6
                            color={skinVars.colors.textPrimaryInverse}
                        >{`${customerData?.data.name} ${customerData?.data.lastname1} ${customerData?.data.lastname2}`}</Text6>
                    </div>
                )}
                {isLight && (
                    <div className='profile-page__header-clientLight'>
                        <Text3 regular color={skinVars.colors.textPrimaryInverse}>
                            {t('profileNoHeadlineIsLight')}
                        </Text3>
                    </div>
                )}
            </div>

            <div className='profile-page__content'>
                <div className='profile-page__content-obd'>
                    {typeUser !== ETypeUser.PRECLIENT && obdEnabled && (
                        <BoxedRow
                            asset={<IconShieldCrossRegular size={24} />}
                            headline={
                                <Tag
                                    type={
                                        customerData?.data.contactNumber?.verified || customerData?.data.email?.verified
                                            ? 'success'
                                            : 'inactive'
                                    }
                                >
                                    {customerData?.data.contactNumber?.verified || customerData?.data.email?.verified
                                        ? t('profileCardObdTagVerified')
                                        : t('profileCardObdTagNotVerified')}
                                </Tag>
                            }
                            title={
                                customerData?.data.contactNumber?.verified || customerData?.data.email?.verified
                                    ? t('profileCardObdTitleVerified')
                                    : t('profileCardObdTitleNotVerified')
                            }
                            subtitle={
                                customerData?.data.contactNumber?.verified || customerData?.data.email?.verified
                                    ? t('profileCardObdSubtitleVerified')
                                    : t('profileCardObdSubtitleNotVerified')
                            }
                            extra={
                                <div className='profile-page__content-obd__btn'>
                                    <ButtonPrimary
                                        style={{ marginRight: '16px' }}
                                        small
                                        onPress={() => {
                                            if (isLight) {
                                                dispatch(showSnackbar());
                                            } else {
                                                onboardingDaon();
                                            }
                                        }}
                                    >
                                        {t('contactVerificationStep1ButtonVerify')}
                                    </ButtonPrimary>
                                    <ButtonLink onPress={() => window.location.reload()}>
                                        {t('contactVerificationStep1CalloutButton')}
                                    </ButtonLink>
                                </div>
                            }
                        ></BoxedRow>
                    )}
                </div>
                <div className='profile-page__content-list'>
                    <RowList>
                        {actions.map((action, index) => (
                            <div key={index}>
                                {size.width >= 1024 ? (
                                    <>
                                        {action.enabled && (
                                            <Row
                                                badge={action.badge}
                                                title={action.title}
                                                description={action.description}
                                                asset={
                                                    <Circle backgroundColor={skinVars.colors.brandLow} size={40}>
                                                        {action.icon}
                                                    </Circle>
                                                }
                                                right={
                                                    <div className='profile-page__content-list__btn'>
                                                        <ButtonPrimary
                                                            small
                                                            onPress={() => {
                                                                handleNavigation(action.redirection.url);
                                                            }}
                                                        >
                                                            {t('btnAcces')}
                                                        </ButtonPrimary>
                                                    </div>
                                                }
                                            />
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {action.enabled && (
                                            <Row
                                                withChevron={true}
                                                badge={action.badge}
                                                title={action.title}
                                                asset={action.icon}
                                                onPress={() => {
                                                    handleNavigation(action.redirection.url);
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                    </RowList>
                </div>
            </div>
        </div>
    );
};

export default Profile;
